/* oxlint-disable no-console */
// libraries
import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
  useContext,
} from "react";
import {
  signInWithEmailAndPassword,
  User,
  GoogleAuthProvider,
  OAuthProvider,
  signInWithRedirect,
  UserCredential,
} from "firebase/auth";

// services
import request from "../../services/request.service";
import persistentStorage from "../../services/persistence.service";
import { shouldPreserveLogoutCache } from "../../services/trusted-computer";
import {
  auth,
  AUTH_TOKEN_STORAGE,
  reinitializeFirestore,
} from "../../services/firebase.service";

// contexts
import { useQuery } from "@tanstack/react-query";
import { useDataStore } from "../dataStore.context";
import {
  AUTH_USER_KEY,
  LOCATION_RESTORE_KEY,
  useLocalStorage,
} from "../../hooks/useLocalStorage";
import { useConnectivityStatus } from "../connectivity.context";

// utilities
import { hasPermission } from "wombat-global/src/utilities/permissions";
import { getDecodedAuthToken } from "./getDecodedAuthToken";
import { fetchClientDocument } from "../../services/firebase/fetchClientDocument";

// constants
import { Time } from "wombat-global/src/utilities/time";
import { ADMIN } from "wombat-global/src/permission.maps";

// types
import { TDecodedToken } from "./authentication.types";
import {
  IClientTarget,
  ISiteTarget,
  isSiteTarget,
  isTenantTarget,
} from "wombat-global/src/typings";
import {
  ES_QUERY_CACHE,
  FB_QUERY_CACHE,
  subscriptionQueryClient,
} from "../react-query/query-client";

// oxlint-disable-next-line max-len
// https://wombat-development.firebaseapp.com/__/auth/handler?apiKey=AIzaSyAJWMU6Z3OpoXi3HWEXbYWxy4soJd94l0E&appName=%5BDEFAULT%5D&authType=signInViaPopup&redirectUrl=https%3A%2F%2Fwombat-development.firebaseapp.com%2Flogin&v=10.12.2&eventId=5581279086&providerId=google.com&scopes=profile%2Chttps%3A%2F%2Fwww.googleapis.com%2Fauth%2Fcontacts.readonly

type AuthenticationContextType = {
  isAuthenticated: boolean;

  isAdmin: (
    target: IClientTarget | ISiteTarget | undefined,
  ) => Promise<boolean>;
  hasPermission: (
    target: IClientTarget | ISiteTarget,
    permissionFlag: number,
  ) => boolean;
  // add an isAdmin call to check if user is a target admin (site checks will get site docs and check owner user status)
  logout: (options?: { clearCache?: boolean }) => Promise<void>;
  reload: () => void;
  ssoLogin: (
    provide: "google.com" | "microsoft.com" | string,
    email?: string,
  ) => Promise<void | [User, TDecodedToken]>;
  ssoRedirectHandler: (
    result?: UserCredential | null,
  ) => Promise<void | [User, TDecodedToken]>;
  login: (
    email: string,
    password: string,
  ) => Promise<void | [User, TDecodedToken]>;
  initialized: boolean;
  user: User | null;
  uid?: string;
  authToken: TDecodedToken | null;
  acceptedTC?: string;
};

export const AuthenticationContext = createContext<AuthenticationContextType>({
  isAuthenticated: false,
  hasPermission: () => false,
  isAdmin: async () => false,
  logout: async () => {},
  login: async () => {
    return;
  },
  ssoLogin: async () => {
    return;
  },
  ssoRedirectHandler: async () => {
    return;
  },
  reload: async () => null,
  initialized: false,
  user: null,
  uid: undefined,
  authToken: null,
});

const msftProvider = new OAuthProvider("microsoft.com");
msftProvider.addScope("user.read");
msftProvider.setCustomParameters({ prompt: "select_account" });

const googleProvider = new GoogleAuthProvider();
googleProvider.addScope("profile");
googleProvider.addScope("email");
googleProvider.setCustomParameters({ prompt: "select_account" });

export const availableProviders: string[] = [
  msftProvider.providerId,
  googleProvider.providerId,
];

export function getProvider(providerId: string) {
  switch (providerId) {
    case googleProvider.providerId:
      return GoogleAuthProvider;
    default:
      return undefined;
  }
}

export function getSSOProvider(providerId: string) {
  switch (providerId) {
    case googleProvider.providerId:
      return googleProvider;
    case msftProvider.providerId:
      return msftProvider;
    default:
      throw new Error("Provider Id is not supported");
  }
}

export const AuthenticationProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const { cleanStoreData } = useDataStore();
  const online = useConnectivityStatus();
  const [authToken, setAuthToken] = useLocalStorage<TDecodedToken | null>(
    AUTH_TOKEN_STORAGE,
    null,
    {
      removeOnLogout: true,
    },
  );
  const [user, setUser] = useLocalStorage<User | null>(AUTH_USER_KEY, null, {
    removeOnLogout: true,
  });
  const [isAuthenticated, setAuthentication] = useState(false);
  const [initialized, setInitialized] = useState(false);

  // Refresh claims every 30 minutes while visible, even without API activity.
  // onIdTokenChanged synchronizes the refreshed token with the auth context.
  useQuery<TDecodedToken | null>({
    queryKey: ["auth-refresh", auth.currentUser?.uid],
    queryFn: async () => {
      try {
        await auth.authStateReady();
        if (!auth.currentUser) {
          return null;
        }

        if (document.visibilityState !== "visible") {
          throw new Error("Auth claims refresh deferred while page is hidden");
        }
        return (await getDecodedAuthToken(true)) ?? null;
      } catch (err) {
        if (document.visibilityState === "visible") {
          console.error("Error refreshing auth claims", err);
        }
        throw err;
      }
    },
    gcTime: 30 * Time.mins,
    staleTime: (query) => query.state.status === "error" ? 0 : 30 * Time.mins,
    refetchInterval: 30 * Time.mins,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    retry: false,
    enabled: () => Boolean(auth.currentUser && online && document.visibilityState === "visible"),
  });

  /**
   * @private
   *
   * This should be used within the login, onAuth, & reload calls and must be Idempotent
   */
  const setUserAuthentication = useCallback(
    (_user: User, token?: TDecodedToken | undefined) => {
      request.setRefreshHandler(() => _user.getIdToken());
      if (token) {
        setAuthToken(token);
      }
      setUser(_user);
      setAuthentication(true);
    },
    [setAuthToken, setUser],
  );

  /**
   * @public
   *
   * useHasPermission checks if the current user has specified permission (encoded in permission bit flag)
   */
  const userHasPermission = useCallback(
    (target: IClientTarget | ISiteTarget, permissionFlag: number) => {
      if (!authToken?.claims) {
        return false;
      }
      return hasPermission(target, authToken.claims, permissionFlag);
    },
    [authToken],
  );

  const isAdmin = useCallback(
    async (target: IClientTarget | ISiteTarget | undefined) => {
      if (!authToken?.claims) {
        return false;
      }

      let allowed = hasPermission(target, authToken.claims, ADMIN.bitFlag);
      if (isSiteTarget(target) && !allowed) {
        const site = await fetchClientDocument(target);
        allowed = hasPermission(site.owner, authToken.claims, ADMIN.bitFlag);
      }

      if (isTenantTarget(target) && !allowed) {
        const tenant = await fetchClientDocument(target);
        allowed = hasPermission(tenant.owner, authToken.claims, ADMIN.bitFlag);
      }

      return allowed;
    },
    [authToken],
  );

  /**
   * @public
   *
   * reload will reload the user auth token with the new claim (use when user claim is updated in the BE)
   */
  const reload = useCallback(async () => {
    if (auth.currentUser) {
      const token = await getDecodedAuthToken(true);
      setUserAuthentication(auth.currentUser, token);
    }
  }, [setUserAuthentication]);

  /**
   * @public
   *
   * logout
   */
  const logout = useCallback(async (options?: { clearCache?: boolean }) => {
    const userId = auth.currentUser?.uid;
    const preserveCache = shouldPreserveLogoutCache(userId, options?.clearCache);
    await auth.signOut();
    // Always end the session, even when this computer retains application data.
    request.setRefreshHandler(undefined);
    setAuthentication(false);
    setAuthToken(null);
    setUser(null);
    cleanStoreData();
    if (!preserveCache) {
      await reinitializeFirestore();
      subscriptionQueryClient.clear();
      await Promise.allSettled([
        persistentStorage.delete(ES_QUERY_CACHE),
        persistentStorage.delete(FB_QUERY_CACHE),
      ]);
      localStorage.removeItem(LOCATION_RESTORE_KEY);
      localStorage.removeItem(ES_QUERY_CACHE);
      localStorage.removeItem(FB_QUERY_CACHE);
    }
    return;
  }, [cleanStoreData, setAuthToken, setUser]);

  /**
   * @public
   *
   * login functions
   */
  const login = useCallback(
    async (
      email: string,
      password: string,
    ): Promise<void | [User, TDecodedToken]> => {
      await signInWithEmailAndPassword(auth, email, password);
      const userEntity = auth.currentUser;

      // decode token
      const tokenResult = await getDecodedAuthToken();

      if (userEntity && tokenResult) {
        cleanStoreData();
        await setUserAuthentication(userEntity, tokenResult);
        return [userEntity, tokenResult];
      }
      return;
    },
    [cleanStoreData, setUserAuthentication],
  );

  const ssoLogin = useCallback(
    async (
      providerType: "google.com" | "microsoft.com" | string,
      email?: string,
    ) => {
      const provider = getSSOProvider(providerType);
      if (email) {
        provider.setCustomParameters({
          login_hint: email,
        });
      }
      await signInWithRedirect(auth, provider);
    },
    [],
  );

  const ssoRedirectHandler = useCallback(
    async (
      result?: UserCredential | null,
    ): Promise<void | [User, TDecodedToken]> => {
      // console.log("1: sso redirect handler start");
      if (result && result.user) {
        const _user = result.user;
        // console.log("2: getting token result");
        const tokenResult = await getDecodedAuthToken();
        // console.log("3: clearing store data");
        cleanStoreData();
        // console.log("4: setting user auth");
        setUserAuthentication(_user, tokenResult);
        return [_user, tokenResult!];
      } else {
        // oxlint-disable-next-line no-console
        console.warn("No redirect result or user is not authenticated yet.");
        throw new Error("Single Sign On Error: No redirect Results");
      }
    },
    [cleanStoreData, setUserAuthentication],
  );

  useEffect(() => {
    let active = true;
    let tokenChangeVersion = 0;

    // Includes sign-in, sign-out, and token refreshes synced from other tabs.
    const authUnsubscribe = auth.onIdTokenChanged(
      async function (userEntity) {
        const version = ++tokenChangeVersion;
        if (userEntity) {
          try {
            // Do not force a refresh inside the token change listener.
            const tokenResult = await getDecodedAuthToken();

            if (
              !active ||
              version !== tokenChangeVersion ||
              auth.currentUser !== userEntity
            ) {
              return;
            }
            setUserAuthentication(userEntity, tokenResult);
          } catch (err) {
            console.error("Error syncing auth token", err);
          }
        } else {
          request.setRefreshHandler(undefined);
          setAuthentication(false);
        }

        if (active && version === tokenChangeVersion) {
          setInitialized(true);
        }
      },
    );

    return () => {
      active = false;
      authUnsubscribe();
    };
  }, [setUserAuthentication]);

  /*
   * Notes: Is the use of useMemo necessary here?
   * IMO yes. Without the memoizing the value passed into the provider an unintentional re-render can be
   * triggered without any value within the object having been changed; as a result, any child component would
   * be re-render. This is because defining the an object within a Components property will create a new
   * version of that object no matter the change (or not) of its content. Possible unintentional triggers
   * would be a parent component re-rendering or an internal method / object (async call?) updating.
   *
   * Its unlikely that a parent to this component will re-render and its internals are simple enough to
   * guarantee they wont cause a re-render, but we add it here to set a standard for other Contexts
   */
  const providerState = useMemo(
    () => ({
      isAuthenticated: online ? isAuthenticated : Boolean(authToken),
      initialized,
      reload,
      logout,
      login,
      ssoLogin,
      ssoRedirectHandler,
      hasPermission: userHasPermission,
      isAdmin,
      acceptedTC: authToken?.claims?.tc,
      user,
      uid: user?.uid,
      authToken,
    }),
    [
      online,
      isAuthenticated,
      authToken,
      initialized,
      reload,
      logout,
      login,
      ssoLogin,
      ssoRedirectHandler,
      userHasPermission,
      isAdmin,
      user,
    ],
  );

  return (
    <AuthenticationContext.Provider value={providerState}>
      {children}
    </AuthenticationContext.Provider>
  );
};

export const useAuthenticationContext = (): AuthenticationContextType => {
  return useContext(AuthenticationContext);
};
